/* global mapboxgl, MapboxDraw, i18n */
// TODO MAKE THIS COMPONENT
import ApplicationController from "application_controller.js";
import { useI18n, useAssetLoader, useMemberPopup, useGeojsonBounds, MEMBER_POPUP_CLASS_NAME } from "mixins";
import { useDebounce } from "stimulus-use";

import translations from "../../../locales/view_components/map.json";
import mapboxgl from "mapbox-gl";
import MapboxGLButtonControl from "../../../mapbox";

const GEOJSON_SOURCE_ID = "place-geojson";

//* The place's own shape, drawn in the site's primary yellow so it reads over
//* both the land and the sea of the Mapbox style.
const GEOJSON_COLOR = "#FDD412";
const GEOJSON_POINT_STROKE_COLOR = "#000000";

export default class extends ApplicationController {
  debug = false;

  static targets = [
    "map",

    "id",
    "latitude",
    "longitude",
    "addPointButton",

    "mapZoom",
    "mapLatitude",
    "mapLongitude",
    "getMapCenter",

    "geojson",
  ];

  static values = {
    mapboxUsername: String,
    mapboxStyleId: String,
    mapboxAccessToken: String,

    zoom: { type: Number, default: 14 },
    latitude: Number,
    longitude: Number,

    geojson: { type: Object, default: {} },
    businessesGeojson: { type: Object, default: {} },
    businessIcons: { type: Object, default: {} },
    maxPoints: { type: Number, default: -1 },
    maxPolygons: { type: Number, default: -1 },
    maxLines: { type: Number, default: -1 },
  };

  static debounces = [];

  static classes = ["hidden"];

  /**
   * Initializes the component by counting the features
   * based on the provided GeoJSON value.
   * @function
   * @public
   * @returns {void}
   */
  initialize() {
    this.log_function("initialize");

    // this.countFeatures(this.geojsonValue);
  }

  /**
   * Establishes connections downloads css assets and generates the map.
   * @function
   * @public
   * @returns {void}
   */
  connect() {
    this.log_function("connect");

    useDebounce(this);
    useAssetLoader(this);
    useMemberPopup(this);
    useGeojsonBounds(this);
    useI18n({
      controller: this,
      translations: translations,
      scope: "view_components.map",
    });

    this.load_style("/node_modules/mapbox-gl/dist/mapbox-gl.css");

    this.generateMap();
  }

  /**
   * Generates and initializes a Mapbox map with specified configurations.
   * @function
   * @public
   * @returns {void}
   */
  generateMap() {
    this.log_function("generateMap");

    this.map = new mapboxgl.Map({
      accessToken: this.mapboxAccessTokenValue,
      container: this.mapTarget,
      style: `mapbox://styles/${this.mapboxUsernameValue}/${this.mapboxStyleIdValue}`,
      exitFullscreenOnEsc: true,
      locale: {
        "AttributionControl.MapFeedback": this.t("controls.attribution.feedback"),
        "AttributionControl.ToggleAttribution": this.t("controls.attribution.toggle"),
        "FullscreenControl.Enter": this.t("controls.fullscreen.enter"),
        "FullscreenControl.Exit": this.t("controls.fullscreen.exit"),
        "GeolocateControl.FindMyLocation": this.t("controls.geolocate.find_my_location"),
        "GeolocateControl.LocationNotAvailable": this.t("controls.geolocate.location_not_available"),
        "LogoControl.Title": this.t("controls.logo.title"),
        "Map.Title": this.t("title"),
        "NavigationControl.ResetBearing": this.t("controls.navigation.reset_bearing"),
        "NavigationControl.ZoomIn": this.t("controls.navigation.zoom_in"),
        "NavigationControl.ZoomOut": this.t("controls.navigation.zoom_out"),
        "ScrollZoomBlocker.CmdMessage": this.t("scroll_zoom_blocker.cmd_message"),
        "ScrollZoomBlocker.CtrlMessage": this.t("scroll_zoom_blocker.ctrl_message"),
        "TouchPanBlocker.Message": this.t("touch_pan_blocker.message"),
      },
    });

    if (this.zoomValue) {
      const zoom = this.zoomValue;
      this.map.setZoom(zoom);
    }

    if (this.longitudeValue && this.latitudeValue){
      const center = [this.longitudeValue, this.latitudeValue];
      this.map.setCenter(center);
    }

    const navigationControlOptions = {
      showCompass: false,
      showZoom: true,
      visualizePitch: true,
    };
    this.map.addControl(new mapboxgl.NavigationControl(navigationControlOptions), "bottom-right");

    const scale = new mapboxgl.ScaleControl({
      maxWidth: 200,
      unit: "metric",
    });
    this.map.addControl(scale);


    this.map.addControl(new mapboxgl.FullscreenControl(), "top-right");


    const ctrlCenter = new MapboxGLButtonControl({
      className: "mapboxgl-ctrl-home",
      title: `${this.t("controls.center.title")}`,
      eventHandler: this.resetMapCenter.bind(this),
    });
    this.map.addControl(ctrlCenter, "bottom-right");

    // this.map.on("zoom", (event) => {});
    // this.map.on("move", (event) => {});
    // this.map.on("draw.create", (event) => {});
    // this.map.on("draw.update", (event) => {});


    //* Disables map rotation using right click + drag
    this.map.dragRotate.disable();

    //* Disables map rotation using touch rotation gesture
    this.map.touchZoomRotate.disableRotation();

    //* Disables the "box zoom" interaction.
    this.map.boxZoom.disable();

    const show_points_button = (this.maxPointsValue > 0 || this.maxPointsValue === -1) && !this.single_point_mode;
    const show_line_button = this.maxLinesValue > 0 || this.maxLinesValue === -1;
    const show_polygon_button = this.maxPolygonsValue > 0 || this.maxPolygonsValue === -1;
    const show_trash_button = show_points_button || show_line_button || show_polygon_button;

    let mode = "simple_select";

    /* istanbul ignore next -- leftover from the admin editor: nothing sets this.mode or this.pointsCount on this read-only map */
    if((this.mode === "single_point") && this.pointsCount === 0) {
      mode = "draw_point";
    }







    this.map.on("load", this.handleMapLoad.bind(this));
    // this.map.on("click", this.handleMapClick.bind(this));
  }



  /**
   * Handles the map load event, drawing the GeoJSON features if valid.
   * @function
   * @public
   * @returns {void}
   */
  handleMapLoad() {
    this.log_function("handleMapLoad");

    if (this.isGeojsonValid(this.geojsonValue)) {
      this.drawGeojson(this.geojsonValue);
    }

    if (this.isGeojsonValid(this.businessesGeojsonValue)) {
      this.drawBusinesses(this.businessesGeojsonValue);
    }

    this.fitToGeojson();
  }



  /**
   * Pins every GoThassos member the place holds: a marker carrying the icon of
   * its listing, opening a popup with the member's name and a link to its page.
   * DOM markers rather than a symbol layer because the icons are FontAwesome
   * SVG rendered server-side, which the Mapbox style's sprite has no images for.
   * @function
   * @private
   * @param {object} geojson - The GeoJSON FeatureCollection of member points.
   * @returns {void}
   */
  drawBusinesses(geojson) {
    this.log_function("drawBusinesses", geojson);

    this.businessMarkers = geojson.features.map((feature) => {
      const element = document.createElement("div");
      element.className = `place-map-marker place-map-marker-${feature.properties.kind}`;
      //* Trusted markup: the icons come from this page's own component
      element.innerHTML = this.businessIconsValue[feature.properties.kind] || "";

      //* The listing map's member popup, so a member reads the same wherever
      //* it is pinned. The offset clears the marker's disc.
      const popup = new mapboxgl.Popup({ offset: 20, maxWidth: "260px", className: MEMBER_POPUP_CLASS_NAME })
        .setHTML(this.memberPopupHtml(feature.properties));

      return new mapboxgl.Marker({ element })
        .setLngLat(feature.geometry.coordinates)
        .setPopup(popup)
        .addTo(this.map);
    });
  }



  /**
   * Draws the GeoJSON features as read-only layers. A village or a beach is a
   * polygon, so it gets an outline and a wash of fill; a walking path is a line
   * and gets the outline alone; anything mapped as a single point gets a dot.
   * @function
   * @private
   * @param {object} geojson - The GeoJSON FeatureCollection to draw.
   * @returns {void}
   */
  drawGeojson(geojson) {
    this.log_function("drawGeojson", geojson);

    this.map.addSource(GEOJSON_SOURCE_ID, { type: "geojson", data: geojson });

    this.map.addLayer({
      id: `${GEOJSON_SOURCE_ID}-fill`,
      type: "fill",
      source: GEOJSON_SOURCE_ID,
      filter: ["==", ["geometry-type"], "Polygon"],
      paint: {
        "fill-color": GEOJSON_COLOR,
        "fill-opacity": 0.2,
      },
    });

    this.map.addLayer({
      id: `${GEOJSON_SOURCE_ID}-line`,
      type: "line",
      source: GEOJSON_SOURCE_ID,
      filter: ["in", ["geometry-type"], ["literal", ["Polygon", "LineString"]]],
      paint: {
        "line-color": GEOJSON_COLOR,
        "line-width": 3,
      },
    });

    this.map.addLayer({
      id: `${GEOJSON_SOURCE_ID}-point`,
      type: "circle",
      source: GEOJSON_SOURCE_ID,
      filter: ["==", ["geometry-type"], "Point"],
      paint: {
        "circle-radius": 7,
        "circle-color": GEOJSON_COLOR,
        "circle-stroke-width": 2,
        "circle-stroke-color": GEOJSON_POINT_STROKE_COLOR,
      },
    });
  }



  /**
   * Frames the map on everything it draws — the place's shape and the members
   * pinned in it, which for a beach can stand outside its outline. This
   * overrides the centre and zoom the component was rendered with; those are
   * the whole-island fallback for a place with nothing of its own to show.
   * @function
   * @private
   * @returns {void}
   */
  fitToGeojson() {
    this.log_function("fitToGeojson");

    const bounds = this.geojsonBounds(this.geojsonValue, this.businessesGeojsonValue);
    if (!bounds) return;

    this.map.fitBounds(bounds, { padding: 40, maxZoom: 15 });
  }



  /**
   * Checks if the provided GeoJSON is valid.
   * @function
   * @private
   * @param {object} geojson - The GeoJSON data to be validated.
   * @returns {boolean} - True if the GeoJSON is valid, false otherwise.
   */
  isGeojsonValid(geojson) {
    this.log_function("isGeojsonValid", geojson);

    if(!geojson) return false;
    return geojson && geojson?.type === "FeatureCollection" && geojson?.features?.length > 0;
  }



  /**
   * Returns the map to the framing it opened with — the place's own shape, or
   * the centre and zoom the component was rendered with when it has none.
   * @function
   * @public
   * @returns {void}
   */
  resetMapCenter() {
    this.log_function("resetMapCenter");

    if (this.isGeojsonValid(this.geojsonValue) || this.isGeojsonValid(this.businessesGeojsonValue)) {
      this.fitToGeojson();
      return;
    }

    this.map.flyTo({
      center: [this.longitudeValue, this.latitudeValue],
      zoom: this.zoomValue,
    });
  }



  /**
   * Disconnects and removes the associated map if it exists.
   * @function
   * @public
   * @returns {void}
   */
  disconnect(){
    this.log_function("disconnect");

    if(this.map) this.map.remove();
  }
}
